import { byAttrVal, createCustomElement, DOMModel, registerEvent } from '@adobe/react-webcomponent'
import { ModalContextProvider } from '@billy/design-system'
import { QueryClientProvider } from 'react-query'

import { RemoteFeatureFlagProvider } from '@features/remoteFeatureFlags'
import { AccountsContextProvider } from '@modules-deprecated/app/accounts'
import {
  UserOrganizationContextProvider,
  UserOrganizationSettingsContextProvider,
} from '@modules-deprecated/app/organization'
import { UserOrganizationsContextProvider } from '@modules-deprecated/app/organizations'
import { TaxRatesContextProvider } from '@modules-deprecated/app/taxrates'
import {
  IsTrustedPartnerContextProvider,
  UmbrellaContextProvider,
  UserUmbrellasContextProvider,
} from '@modules-deprecated/app/umbrellas'
import { CurrentUserContextProvider } from '@modules-deprecated/app/user'
import { ContactsEmberConnectionContextProvider } from '@views/contacts/context/contactsEmberConnectionContext'
import { SubscriptionPlansContextProvider } from '@views/settings/routes/OrganizationSubscriptionSettings/contexts/subscriptionPlansContext'
import { TransactionsEmberConnectionContextProvider } from '@views/transactions/contexts/transactionsEmberConnectionContext'
import { ViewContainer } from '@views/viewContainer/ViewContainer'

import { queryClient } from '../../config/queryClient'
import { BillsEmberConnectionContextProvider } from '../../contexts/billsEmberConnectionContext'
import { DirtyRouteContextProvider, DirtyRouteProps } from '../../contexts/dirtyRouteContext'
import { EmberCurrentRouteContextProvider } from '../../contexts/emberCurrentRouteContext'
import { EmberUserTypeContextProvider } from '../../contexts/emberUserTypeContext'
import { GlobalFiscalYearContextProvider } from '../../contexts/globalFiscalYearContext'
import { ThemeProvider } from '../../contexts/themeProvider'
import { EmberEventFn } from '../../types/emberEventFn'
import { HistoryRouter } from '../../utils/HistoryRouter'

const MODULE_NAME = 'view-container-module'

class ViewContainerWebComponent extends DOMModel {
  @registerEvent('billsConnectBank') billsConnectBank: Function
  @registerEvent('billsInvalidateBill') billsInvalidateBill: Function
  @registerEvent('leaveRoute') leaveRoute: Function
  @registerEvent('setDirtyRouteFlag') setDirtyRouteFlag: Function
  @registerEvent('createContact') createContact: Function
  @registerEvent('editContact') editContact: Function
  @registerEvent('addTransaction') addTransaction: Function
  @byAttrVal('currentroutename') currentRouteName: string
  @byAttrVal('currentroutepath') currentRoutePath: string
  @byAttrVal('features') features: string
  @byAttrVal('organizationid') organizationId: string
  @byAttrVal('umbrellaid') umbrellaId: string
  @byAttrVal('usertype') userType: string
}

interface MenuModuleProps extends DirtyRouteProps {
  currentRouteName: string
  currentRoutePath: string
  features: string
  organizationId: string
  userType: string
  isBetaBillsEnabled: boolean
  onBillsInvalidateBill: EmberEventFn
  onResetAndGoToBill: EmberEventFn
  onCreateContact: EmberEventFn
  onEditContact: EmberEventFn
  onAddTransaction: EmberEventFn
  umbrellaId: string
}

const MenuModule = ({
  currentRouteName,
  currentRoutePath,
  organizationId,
  umbrellaId,
  userType,
  onBillsInvalidateBill,
  onLeaveRoute,
  onSetDirtyRouteFlag,
  onCreateContact,
  onEditContact,
  onAddTransaction,
}: MenuModuleProps) => {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <HistoryRouter>
          <ModalContextProvider>
            <DirtyRouteContextProvider onLeaveRoute={onLeaveRoute} onSetDirtyRouteFlag={onSetDirtyRouteFlag}>
              <UmbrellaContextProvider umbrellaId={umbrellaId}>
                <CurrentUserContextProvider>
                  <UserUmbrellasContextProvider>
                    <IsTrustedPartnerContextProvider organizationId={organizationId}>
                      <UserOrganizationsContextProvider>
                        <UserOrganizationContextProvider organizationId={organizationId}>
                          <RemoteFeatureFlagProvider>
                            <UserOrganizationSettingsContextProvider organizationId={organizationId}>
                              <BillsEmberConnectionContextProvider onInvalidateBill={onBillsInvalidateBill}>
                                <ContactsEmberConnectionContextProvider
                                  onCreateNewContact={onCreateContact}
                                  onEditContact={onEditContact}
                                >
                                  <GlobalFiscalYearContextProvider organizationId={organizationId}>
                                    <AccountsContextProvider organizationId={organizationId}>
                                      <TaxRatesContextProvider organizationId={organizationId}>
                                        <TransactionsEmberConnectionContextProvider onAddTransaction={onAddTransaction}>
                                          <EmberCurrentRouteContextProvider
                                            currentRouteName={currentRouteName}
                                            currentRoutePath={currentRoutePath}
                                          >
                                            <EmberUserTypeContextProvider userType={userType}>
                                              {/* SubscriptionPlansContextProvider is needed here to ensure subscription plans prices are available on app load, do not remove */}
                                              <SubscriptionPlansContextProvider>
                                                <ViewContainer />
                                              </SubscriptionPlansContextProvider>
                                            </EmberUserTypeContextProvider>
                                          </EmberCurrentRouteContextProvider>
                                        </TransactionsEmberConnectionContextProvider>
                                      </TaxRatesContextProvider>
                                    </AccountsContextProvider>
                                  </GlobalFiscalYearContextProvider>
                                </ContactsEmberConnectionContextProvider>
                              </BillsEmberConnectionContextProvider>
                            </UserOrganizationSettingsContextProvider>
                          </RemoteFeatureFlagProvider>
                        </UserOrganizationContextProvider>
                      </UserOrganizationsContextProvider>
                    </IsTrustedPartnerContextProvider>
                  </UserUmbrellasContextProvider>
                </CurrentUserContextProvider>
              </UmbrellaContextProvider>
            </DirtyRouteContextProvider>
          </ModalContextProvider>
        </HistoryRouter>
      </ThemeProvider>
    </QueryClientProvider>
  )
}

const MenuElement = createCustomElement(MenuModule, ViewContainerWebComponent, 'element')

customElements.get(MODULE_NAME) || customElements.define(MODULE_NAME, MenuElement)

export default MenuElement
